import {
  CSSProperties,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {
  motion,
  MotionValue,
  PanInfo,
  useAnimationControls,
  useTransform
} from 'framer-motion';
import {
  cloudStorage,
  hapticFeedbackImpactOccurred
} from '@telegram-apps/sdk-react';
import { Trans, useTranslation } from 'react-i18next';

import { SwipeRef, SwipeType } from '../Discovery';

import UIConfig from '@/UIConfig';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { useDiscoveryStore } from '@/store/useDiscoveryStore.ts';
import Modal from '@/animations/Modal.tsx';
import PaywallPopup from '@/components/Popup/PaywallPopup/PaywallPopup.tsx';
import { PaywallTypes, ProductInvoicePlacements } from '@/enums/apiEnums.ts';
import { CustomDiscoveryCardHids } from '@/enums/viewEnums.ts';
import { CustomDiscoveryCardCloudKeys } from '@/enums/storageEnums.ts';
import { ImageWithLoader } from '@/components/common/Image/ImageWithLoader.tsx';
import LoveShape from '@/components/common/svg/shapes/LoveShape.tsx';
import FallingStarShape from '@/components/common/svg/shapes/FallingStarShape.tsx';
import SmallStarsShape from '@/components/common/svg/shapes/SmallStarsShape.tsx';
import { helpers } from '@/helpers.ts';

interface PremiumUpsaleCardProps {
  isFrontCard: boolean;
  sharedX: MotionValue<number>;
}

// eslint-disable-next-line react/display-name
const PremiumUpsaleCard = forwardRef<SwipeRef, PremiumUpsaleCardProps>(
  ({ isFrontCard, sharedX }, ref) => {
    const { t } = useTranslation();

    const [payWallOpen, setPayWallOpen] = useState<boolean>(false);
    const [pendingRemoval, setPendingRemoval] = useState<boolean>(false);

    const animControls = useAnimationControls();
    const cardRef = useRef<HTMLDivElement>(null);

    const backCardScale = useTransform(sharedX, [-150, 0, 150], [1, 0.9, 1]);

    const { removeCandidate } = useDiscoveryStore();
    const { aboutMe } = useProfileStore();

    const openPayWall = () => setPayWallOpen(true);

    const closePayWall = async () => {
      setPayWallOpen(false);
      if (pendingRemoval) {
        await removeCandidate(CustomDiscoveryCardHids.PREMIUM_UPSALE);
        try {
          await cloudStorage.setItem(
            CustomDiscoveryCardCloudKeys.PREMIUM_UPSALE_KEY,
            Date.now().toString()
          );
        } catch (e) {
          /* error */
        }
        setPendingRemoval(false);
      }
    };

    const onClickPass = () => isFrontCard && swipeLeft(SwipeType.SLOW);

    const handleSwipe = async (direction: 'right' | 'left') => {
      if (direction === 'right') {
        setPendingRemoval(true);
        openPayWall();
      } else {
        await removeCandidate(CustomDiscoveryCardHids.PREMIUM_UPSALE);
        try {
          await cloudStorage.setItem(
            CustomDiscoveryCardCloudKeys.PREMIUM_UPSALE_KEY,
            Date.now().toString()
          );
        } catch (e) {
          /* error */
        }
      }
    };

    const swipeRight = ({
      type
    }: {
      type?: SwipeType;
      ignoreLikeRequest?: boolean;
    }) => {
      hapticFeedbackImpactOccurred('rigid');
      const duration = type === SwipeType.SLOW ? 0.6 : 0.4;
      cardRef.current?.classList.add('swiping');

      void animControls
        .start({
          x: 500,
          rotate: 20,
          transition: { duration },
          transitionEnd: { display: 'none' }
        })
        .then(() => {
          cardRef.current?.classList.remove('swiping');
          void handleSwipe('right');
        });
    };

    const swipeLeft = (type: SwipeType) => {
      hapticFeedbackImpactOccurred('rigid');
      const duration = type === SwipeType.SLOW ? 0.6 : 0.4;
      cardRef.current?.classList.add('swiping');

      void animControls
        .start({
          x: -500,
          rotate: -20,
          transition: { duration },
          transitionEnd: { display: 'none' }
        })
        .then(() => {
          cardRef.current?.classList.remove('swiping');
          void handleSwipe('left');
        });
    };

    useImperativeHandle(ref, () => ({
      swipeRight,
      swipeLeft
    }));

    const snapBack = () => {
      void animControls.start({ x: 0, transition: { duration: 0.3 } });
    };

    const onDragEnd = (
      _: MouseEvent | TouchEvent | PointerEvent,
      info: PanInfo
    ) => {
      if (Math.abs(info.offset.x) > 150) {
        hapticFeedbackImpactOccurred('rigid');
        if (info.offset.x > 0) {
          swipeRight({ type: SwipeType.FAST });
        } else {
          swipeLeft(SwipeType.FAST);
        }
      } else {
        snapBack();
      }
    };

    const handleButtonClick = useCallback(() => {
      setPendingRemoval(true);
      openPayWall();
    }, []);

    return (
      <>
        <motion.div
          ref={cardRef}
          drag={isFrontCard}
          style={{
            x: isFrontCard ? sharedX : undefined,
            scale: !isFrontCard ? backCardScale : 1,
            ...containerStyle
          }}
          animate={animControls}
          dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
          dragSnapToOrigin
          onDragEnd={onDragEnd}
        >
          <div className="cardContent" style={contentStyle}>
            <div style={cardBackgroundStyle}>
              <div style={cardContentWrapperStyle}>
                <div style={circlesContainerStyle}>
                  <div style={outerCircleStyle}>
                    <div style={innerCircleStyle}>
                      <div style={profileImageContainerStyle}>
                        <ImageWithLoader
                          src={helpers.getPrimaryPhoto(
                            aboutMe?.profile.v2photos?.[0]?.variants ?? []
                          )}
                          alt=""
                          style={profileImageStyle}
                        />
                        <div style={diamondBadgeContainerStyle}>
                          <LoveShape
                            style={{ marginTop: '3px', marginLeft: '1px' }}
                            fillColor="#ffffff"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ position: 'absolute', top: -15, left: -35 }}>
                    <FallingStarShape height={40} width={40} />
                  </div>
                  <div style={{ position: 'absolute', bottom: 0, right: 0 }}>
                    <SmallStarsShape height={30} width={30} />
                  </div>
                </div>
                <h2 style={headingStyle}>
                  <Trans
                    i18nKey="custom_premium_card.title"
                    components={{
                      br: <br />
                    }}
                  />
                </h2>
                <p style={paragraphStyle}>
                  <Trans
                    i18nKey="custom_premium_card.subtitle"
                    components={{
                      bold: <b />,
                      br: <br />
                    }}
                  />
                </p>
                <motion.div
                  style={buttonContainerStyle}
                  onClick={handleButtonClick}
                  whileTap={{ scale: 0.95 }}
                >
                  <span style={buttonTextStyle}>
                    {t('custom_premium_card.button')}
                  </span>
                </motion.div>
                <div style={passTextStyle} onClick={onClickPass}>
                  <p>{t('custom_premium_card.pass_button')}</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        <Modal
          fullScreen
          scrollable
          open={payWallOpen}
          handleClose={closePayWall}
        >
          <PaywallPopup
            handleClose={closePayWall}
            type={PaywallTypes.TYPE_SUBSCRIPTION}
            placement={
              ProductInvoicePlacements.MATCHMAKER_BOOST_LIKES_UPSALE_CARD
            }
          />
        </Modal>
      </>
    );
  }
);

// Styles (unchanged)
const containerStyle: CSSProperties = {
  position: 'absolute',
  width: '100%',
  height: 'calc(100vh - 180px)',
  marginTop: '100px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  touchAction: 'none',
  borderRadius: '35px',
  overflow: 'hidden'
} as const;

const contentStyle: CSSProperties = {
  width: 'calc(100% - 25px)',
  height: 'calc(100% - 25px)',
  background: UIConfig.Colors.BackgroundMedium,
  borderRadius: '35px',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
} as const;

const cardBackgroundStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  position: 'relative',
  borderRadius: '35px',
  background:
    'radial-gradient(84.64% 84.64% at 5.43% 5.01%, #8183FC 0%, #A679EB 25.37%, #D66DD6 100%)'
} as const;

const cardContentWrapperStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  textAlign: 'center'
} as const;

const circlesContainerStyle: CSSProperties = {
  position: 'relative',
  width: 'fit-content'
} as const;

const outerCircleStyle: CSSProperties = {
  width: 164,
  height: 164,
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.03)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
} as const;

const innerCircleStyle: CSSProperties = {
  width: 136,
  height: 136,
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.09)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
} as const;

const profileImageContainerStyle: CSSProperties = {
  position: 'relative',
  width: '106px',
  height: '106px',
  borderRadius: '50%',
  border: '4px solid #fff'
} as const;

const profileImageStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '50%'
} as const;

const diamondBadgeContainerStyle: CSSProperties = {
  position: 'absolute',
  width: '40px',
  height: '40px',
  background: '#89319E',
  bottom: -17,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '50%',
  border: '3px solid #ffffff'
} as const;

const headingStyle: CSSProperties = {
  margin: '10px 0 14px',
  textAlign: 'center',
  color: '#fff'
} as const;

const paragraphStyle: CSSProperties = {
  margin: 0,
  maxWidth: '70%',
  color: '#fff',
  fontSize: '14px',
  textAlign: 'center'
} as const;

const buttonContainerStyle: CSSProperties = {
  borderRadius: '20px',
  display: 'flex',
  minWidth: '50%',
  minHeight: '40px',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: '40px',
  background: '#fff',
  cursor: 'pointer'
} as const;

const buttonTextStyle: CSSProperties = {
  fontSize: '15px',
  padding: '11px 18px',
  fontWeight: 'bold',
  color: '#802B95',
  fontFamily: UIConfig.Fonts.Primary
} as const;

const passTextStyle: CSSProperties = {
  marginTop: 7,
  color: '#ffffff',
  opacity: 0.8,
  fontSize: 14,
  fontWeight: 'bold'
} as const;

export default PremiumUpsaleCard;
